/**
 * Trusted origins for storefront ↔ admin iframe {@code postMessage}.
 *
 * <p>The storefront preview iframe is cross-origin from the admin UI
 * ({@code :8000} ↔ {@code :8001} locally). {@code postMessage(..., '*')} is
 * Sonar S2819; these helpers pin a concrete target origin on send and reject
 * untrusted senders on receive.
 */

/** Local admin UI origin; {@code serve.js} substitutes this string in deployed pods. */
const DEFAULT_ADMIN_ORIGIN = 'http://localhost:8001';

const TRUSTED_HOST_SUFFIXES = ['.germanos.gr', '.cosmote.gr', '.ote.gr'] as const;

/**
 * Returns true when {@code hostname} is a Cosmote/Germanos host or local admin.
 *
 * @param hostname a URL hostname
 * @return whether the host may send or receive preview {@code postMessage}
 */
function isTrustedHost(hostname: string): boolean {
  const host = hostname.toLowerCase().replace(/(^\[)|(\]$)/g, '');
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return true;
  }
  return TRUSTED_HOST_SUFFIXES.some((suffix) => host === suffix.slice(1) || host.endsWith(suffix));
}

/**
 * Parses {@code value} to an origin when the host is trusted.
 *
 * @param value an absolute URL, origin, or empty value
 * @return the origin, or {@code null}
 */
function originFromUrl(value: string | undefined): string | null {
  if (!value) {
    return null;
  }
  try {
    const url = new URL(value);
    if (url.protocol !== 'http:' && url.protocol !== 'https:') {
      return null;
    }
    if (!isTrustedHost(url.hostname)) {
      return null;
    }
    return url.origin;
  } catch {
    return null;
  }
}

/**
 * Target origin for messages posted to {@code window.parent} (admin preview).
 *
 * @return a concrete origin — never {@code *}
 */
export function getParentPostMessageOrigin(): string {
  if (typeof window === 'undefined') {
    return DEFAULT_ADMIN_ORIGIN;
  }
  const fromReferrer = originFromUrl(document.referrer);
  if (fromReferrer) {
    return fromReferrer;
  }
  const fromEnv = originFromUrl(process.env.GATSBY_ADMIN_UI_URL);
  if (fromEnv) {
    return fromEnv;
  }
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') {
    return DEFAULT_ADMIN_ORIGIN;
  }
  return window.location.origin;
}

/**
 * Returns true when a {@code message} event came from a trusted admin parent.
 *
 * @param origin {@code MessageEvent.origin}
 * @return whether the payload may be applied
 */
export function isTrustedAdminMessageOrigin(origin: string | undefined): boolean {
  if (!origin) {
    return false;
  }
  if (typeof window !== 'undefined' && origin === window.location.origin) {
    return true;
  }
  return originFromUrl(origin) !== null;
}

/**
 * Returns true when a CMS {@code srcDoc} / same-origin iframe may talk to us.
 *
 * <p>{@code about:srcdoc} serializes {@code event.origin} as the string
 * {@code "null"}. Combined with a {@code contentWindow} source check, that is
 * the only safe extra origin we accept besides our own.
 *
 * @param origin {@code MessageEvent.origin}
 * @return whether origin is same-origin or the srcDoc {@code "null"} sentinel
 */
export function isTrustedCmsIframeMessageOrigin(origin: string | undefined): boolean {
  if (!origin) {
    return false;
  }
  if (typeof window !== 'undefined' && origin === window.location.origin) {
    return true;
  }
  return origin === 'null';
}
